/**
 * Le contrat de l'administration du back-office, côté navigateur — agents,
 * matrice, paramètres et drapeaux de produit (`EX-BO-002`, `EX-BO-004`,
 * `EX-REG-006`).
 *
 * **Ce fichier ne décide de rien.** Les types suivent `AdministrationMessages`,
 * sans rien y ajouter, et un contrôle les confronte champ par champ au fichier
 * Java.
 *
 * ## Ce que ce chantier referme — la dernière fois
 *
 * Deux modules portaient des **écritures** et aucune **lecture** :
 * `BO_USER_CREATE` existait depuis la `V7` sans que rien ne dise quels agents
 * existent, et `FEATURE_FLAG_UPDATE` sans que rien ne dise l'état des drapeaux.
 * C'est la famille « la couche qui permet de **trouver** manquait sous la
 * couche qui permet d'**agir** », et c'est la quatrième fois qu'elle est soldée
 * dans ce chantier — après les fiches clients, les agents de terrain et, pour
 * les promotions, sa déclaration.
 *
 * Quatre routes ont été écrites, et trois droits de lecture posés par `V61`.
 *
 * ## AUCUN SECRET NE SORT
 *
 * `admin.bo_user` porte un condensat de mot de passe et un numéro de téléphone.
 * **Ni l'un ni l'autre n'est au contrat.** Un condensat n'a rien à faire dans
 * un navigateur, et le numéro d'un agent est une donnée personnelle qu'un
 * annuaire n'a pas besoin de montrer pour servir.
 *
 * ## LE DRAPEAU QUI N'OUVRAIT RIEN
 *
 * `FEATURE_FLAG_UPDATE` avait une cellule de matrice, une table, un dépôt — et
 * **aucun exécuteur**. Deux personnes signaient, le journal enregistrait, le
 * socle rendait 200, et aucun produit ne s'ouvrait. `FeatureFlagUpdateExecutor`
 * a été écrit le même jour : sans lui, cet écran aurait menti avec la caution
 * d'une double signature.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin de l'annuaire des agents. */
export const administrationUsersPath = '/api/backoffice/users';

/** Le chemin de la matrice des habilitations. */
export const administrationMatrixPath = '/api/backoffice/roles/matrix';

/** Le chemin des paramètres système. */
export const administrationParametersPath = '/api/backoffice/parameters';

/** Le chemin des drapeaux de produit. */
export const administrationFlagsPath = '/api/backoffice/feature-flags';

/** Le chemin de la route unique des actions habilitables. */
export const administrationActionsPath = '/api/backoffice/actions';

/** Le motif écrit au journal pour les lectures d'agents et de matrice. */
export const usersReason = 'administration-des-acces';

/** Le motif écrit au journal pour les lectures de paramètres et de drapeaux. */
export const settingsReason = 'consultation-des-parametres';

/** Le code d'action de la bascule d'un produit. */
export const featureFlagAction = 'FEATURE_FLAG_UPDATE';

/**
 * Un agent du back-office. Suit `AdministrationMessages.AgentView`.
 *
 * `lockedUntil` et `frozenAt` disent deux choses **différentes** : un verrou est
 * temporaire — trop de mots de passe faux, il tombe tout seul — là où un gel est
 * une décision, qui ne se lève que par une autre décision. Les rendre du même
 * champ ferait confondre un agent maladroit avec un agent écarté.
 */
export interface BackOfficeAgent {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly roleCode: string;
  readonly status: string;
  readonly failedAttempts: number;
  readonly lockedUntil: string | null;
  readonly frozenAt: string | null;
}

/** Un rôle de la matrice. Suit `RoleView`. */
export interface MatrixRole {
  readonly code: string;
  readonly label: string;
  readonly readOnly: boolean;
}

/** Une action habilitable. Suit `ActionView`. */
export interface MatrixAction {
  readonly code: string;
  readonly label: string;
  readonly module: string;
  readonly write: boolean;
}

/**
 * Une cellule de la matrice. Suit `CellView`.
 *
 * `grantKind` vaut `FORBIDDEN`, `ALLOWED` ou `DUAL_APPROVAL`. Les trois sont
 * servies : omettre les refus ferait confondre « interdit » avec « pas encore
 * décrit ».
 */
export interface MatrixCell {
  readonly roleCode: string;
  readonly actionCode: string;
  readonly grantKind: string;
}

/** La matrice entière. Suit `MatrixView`. */
export interface PermissionMatrix {
  readonly roles: readonly MatrixRole[];
  readonly actions: readonly MatrixAction[];
  readonly cells: readonly MatrixCell[];
}

/**
 * Un paramètre système. Suit `ParameterView`.
 *
 * `minValue` et `maxValue` peuvent manquer : tous les paramètres ne sont pas
 * bornés. Les rendre à zéro ferait lire « borné à zéro » là où il faut lire
 * « sans borne » — et c'est sur ces bornes qu'un exploitant décide s'il peut
 * changer une valeur sans risque.
 */
export interface SystemParameter {
  readonly key: string;
  readonly value: string;
  readonly valueType: string;
  readonly domain: string;
  readonly description: string;
  readonly minValue: string | null;
  readonly maxValue: string | null;
  readonly updatedBy: string;
  readonly updatedAt: string;
}

/**
 * Un drapeau de produit. Suit `FeatureFlagView`.
 *
 * `rolloutPercentage` accompagne **toujours** `enabled` : un produit « ouvert »
 * à 3 % du parc n'est pas un produit ouvert.
 */
export interface FeatureFlag {
  readonly code: string;
  readonly enabled: boolean;
  readonly rolloutPercentage: number;
  readonly description: string;
}

/** Ce que rend la route des actions. */
export interface ActionOutcome {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/** Lit l'annuaire des agents du back-office. */
export async function fetchAgents(
  client: ApiClient,
  reason: string = usersReason,
): Promise<readonly BackOfficeAgent[]> {
  const parametres = new URLSearchParams({ reason });
  return exigerUneListe(
    await client.request<readonly BackOfficeAgent[]>(
      `${administrationUsersPath}?${parametres.toString()}`,
    ),
    'les agents',
  );
}

/**
 * Lit la matrice des habilitations.
 *
 * Aucune capture d'erreur : rendre une matrice vide ferait confondre « aucun
 * droit n'est accordé » — qui serait un incident majeur — avec « le serveur a
 * refusé ». Sur une pièce d'audit, la confusion est celle qu'on ne peut pas se
 * permettre.
 */
export async function fetchPermissionMatrix(
  client: ApiClient,
  reason: string = usersReason,
): Promise<PermissionMatrix> {
  const parametres = new URLSearchParams({ reason });
  const matrice = await client.request<PermissionMatrix>(
    `${administrationMatrixPath}?${parametres.toString()}`,
  );
  return exigerUneMatrice(matrice);
}

/** Lit les paramètres système. */
export async function fetchSystemParameters(
  client: ApiClient,
  reason: string = settingsReason,
): Promise<readonly SystemParameter[]> {
  const parametres = new URLSearchParams({ reason });
  return exigerUneListe(
    await client.request<readonly SystemParameter[]>(
      `${administrationParametersPath}?${parametres.toString()}`,
    ),
    'les paramètres',
  );
}

/** Lit les drapeaux de produit. */
export async function fetchFeatureFlags(
  client: ApiClient,
  reason: string = settingsReason,
): Promise<readonly FeatureFlag[]> {
  const parametres = new URLSearchParams({ reason });
  return exigerUneListe(
    await client.request<readonly FeatureFlag[]>(
      `${administrationFlagsPath}?${parametres.toString()}`,
    ),
    'les drapeaux de produit',
  );
}

/**
 * Demande l'ouverture ou la fermeture d'un produit.
 *
 * **En double validation.** Le socle rend `202` avec l'identifiant de la
 * demande, et l'écran ne doit pas annoncer un produit ouvert : c'est ce que
 * `outcome` distingue.
 *
 * `rolloutPercentage` n'est envoyé qu'à l'ouverture : fermer, c'est fermer pour
 * tous, et l'exécuteur remet la part à zéro plutôt que de la conserver — un
 * produit fermé « à 40 % » laisserait croire qu'il rouvrira à 40 %.
 */
export async function switchFeatureFlag(
  client: ApiClient,
  demande: {
    readonly code: string;
    readonly enabled: boolean;
    readonly rolloutPercentage?: number;
    readonly reason: string;
  },
): Promise<ActionOutcome> {
  const charge: Record<string, string> = { enabled: String(demande.enabled) };
  if (demande.enabled && demande.rolloutPercentage !== undefined) {
    charge.rolloutPercentage = String(demande.rolloutPercentage);
  }
  return client.request<ActionOutcome>(
    `${administrationActionsPath}/${featureFlagAction}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityRef: demande.code,
        reason: demande.reason,
        payload: charge,
      }),
    },
  );
}

/**
 * Refuse ce qui n'est **pas** une liste.
 *
 * **Cette garde manquait, et le défaut a rougi dans `routing.test.tsx`** : la
 * carte d'activation du tableau de bord appelait `.filter` sur une réponse qui
 * n'était pas un tableau, et l'exception remontait jusqu'à la racine React —
 * une réponse de travers sur UNE carte effaçait TOUT l'écran. Une lecture
 * refusée doit tomber dans la branche d'erreur de sa carte, pas emporter les
 * cinq autres.
 */
function exigerUneListe<T>(recue: readonly T[], quoi: string): readonly T[] {
  if (!Array.isArray(recue)) {
    throw new Error(`ÉCHEC : la réponse qui devait porter ${quoi} n’est pas une liste.`);
  }
  return recue;
}

/**
 * Refuse ce qui n'est **pas** une matrice.
 *
 * L'écran s'écroulerait sur une lecture de `cells.length`, et un auditeur
 * verrait un écran blanc à l'endroit où il vient vérifier qui peut quoi.
 */
function exigerUneMatrice(matrice: PermissionMatrix): PermissionMatrix {
  if (!Array.isArray(matrice?.roles) || !Array.isArray(matrice?.actions)
    || !Array.isArray(matrice?.cells)) {
    throw new Error(
      'ÉCHEC : la réponse n’est pas une matrice — les rôles, les actions et les ' +
        'cellules sont ce qui la définit.',
    );
  }
  return matrice;
}
